<template>
  <div class="cherenkov-viz">
    
    <div class="viz-header" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula" v-latex>$<strong>公式：</strong>cosθ = 1 / (n·β) | β = v/c$</div>
    </div>

    <div class="main-layout vertical">
      <div class="canvas-box simulation">
        <svg width="100%" height="240" viewBox="0 0 400 240">
          <!-- The Medium (Water-like) -->
          <rect x="0" y="0" width="400" height="240" fill="rgba(0, 111, 253, 0.1)" />

          <!-- The Particle -->
          <circle :cx="posX" cy="120" r="4" fill="#fff">
             <animate attributeName="cx" from="50" to="350" :dur="2 / beta + 's'" repeatCount="indefinite" />
          </circle>

          <!-- The Cone (Shockwave) -->
          <g v-if="beta > 1/1.33">
             <path :d="conePath" fill="none" stroke="#00d2ff" stroke-width="2" opacity="0.6" />
             <path v-for="i in 5" :key="i" :d="calcWavefront(i)" fill="none" stroke="#00d2ff" stroke-width="1" opacity="0.2">
                <animate attributeName="opacity" values="0.3;0;0.3" dur="0.5s" repeatCount="indefinite" />
             </path>
          </g>

          <text x="300" y="40" fill="#00d2ff" font-size="20">✨</text>
        </svg>

        <div class="hud-top">
           <div class="stat">
              <label>介质中光速 c' = c / n</label>
              <div class="value" :style="{ color: isGlowing ? '#00d2ff' : '#888' }">{{ isGlowing ? '产生蓝光辐射' : '未超过介质光速' }}</div>
           </div>
        </div>
      </div>

      <div class="bottom-controls">
        <div class="control-card glass">
          <div class="row items-center">
             <label>粒子速度 v (相对真空光速 c)</label>
             <el-slider v-model="beta" :min="0.5" :max="0.99" step="0.01" size="mini"></el-slider>
          </div>
          <div class="row">
             <label>折射率 n (水 ≈ 1.33)</label>
          </div>
        </div>

        <div class="discovery-note" v-latex>
           <p><strong>物理详解：</strong>虽然真空光速不可超越，但在介质（如水）中，光速会降为 $c/n$。如果带电粒子的速度超过了这一数值，就会产生一种类似“超音速激波”的电磁辐射。其特征是幽幽的**淡蓝色光**，常见于核反应堆的水池中。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CherenkovRadiationViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      beta: 0.8,
      n: 1.33,
      posX: 50
    }
  },
  computed: {
    isGlowing() { return this.beta > (1 / this.n); },
    conePath() {
       if (!this.isGlowing) return "";
       // Angle theta where cos(theta) = 1/(n*beta)
       let theta = Math.acos(1 / (this.n * this.beta));
       let len = 150;
       let x = this.posX;
       let y = 120;
       return `M ${x} ${y} L ${x - len} ${y - len * Math.tan(theta)} M ${x} ${y} L ${x - len} ${y + len * Math.tan(theta)}`;
    }
  },
  methods: {
    calcWavefront(i) {
       if (!this.isGlowing) return "";
       let r = i * 20;
       let x = this.posX - r * (this.n * this.beta);
       return `M ${x+r} 120 A ${r} ${r} 0 1 1 ${x+r} 119`;
    },
    animate() {
       this.posX = 50 + (Date.now() % 2000) / 2000 * 300;
       requestAnimationFrame(this.animate);
    }
  },
  mounted() { this.animate(); },
  beforeDestroy() {
    if (this.animFrame) cancelAnimationFrame(this.animFrame);
    if (this.timer) clearInterval(this.timer);
  },
}
</script>

<style scoped lang="scss">
.cherenkov-viz { flex: 1; display: flex; flex-direction: column; gap: 12px; height: 100%; overflow: hidden; }
.canvas-box { background: #001; border-radius: 12px; position: relative; border: 1px solid rgba(255,255,255,0.05); }
.bottom-controls { flex: 0.6; display: flex; flex-direction: column; gap: 10px; }
.control-card { background: rgba(255,255,255,0.03); border-radius: 12px; padding: 15px; }
.discovery-note { background: rgba(0, 111, 253, 0.05); padding: 12px 18px; border-radius: 12px; p { margin: 0; font-size: 0.75rem; color: #aaa; line-height: 1.5; strong { color: #fff; } } }
</style>
